"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";

import { CTAButton } from "@/shadcn/ui/button";
import ExperienceCard from "@/components/getting-started/ExperienceCard";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { PagePhemexOfferSchema } from "@/sanity/pages/PagePhemexOfferTemplate";
import { PageBitunixOfferSchema } from "@/sanity/pages/PageBitunixOfferTemplate";

import hugeLogoBackgroundImg from "@/public/lp2/experience/huge-logo-bg.svg";
import blurImg from "@/public/lp2/experience/bottom-blur.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function ExperienceSection({
  data,
  bitunix,
  phemex,
}: {
  data: PageHomeSchema | PagePhemexOfferSchema | PageBitunixOfferSchema;
  bitunix?: boolean;
  phemex?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const containerRef2 = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);
  const hugeAssetImgRef = useRef<HTMLDivElement | any>(undefined);
  const content2Ref = useRef<HTMLDivElement | any>(undefined);
  const content3Ref = useRef<HTMLDivElement | any>(undefined);
  const content4Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const experienceTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      if (containerRef?.current) {
        experienceTL.from(".huge-logo-bg", {
          y: "2rem",
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        });
      }

      if (content1Ref?.current) {
        experienceTL.from(content1Ref?.current?.children, {
          y: "3rem",
          stagger: 0.1,
          duration: 0.5,
          delay: 1,
          scrollTrigger: {
            trigger: content1Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
      }

      if (hugeAssetImgRef?.current) {
        experienceTL.from(hugeAssetImgRef.current, {
          y: "3rem",
          delay: 0.25,
          scrollTrigger: {
            trigger: hugeAssetImgRef.current,
            end: "center center",
            scrub: 1,
          },
        });
      }

      if (content2Ref?.current) {
        experienceTL.from(content2Ref?.current?.children, {
          y: "3rem",
          stagger: 0.1,
          delay: 0.25,
          scrollTrigger: {
            trigger: content2Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
      }

      if (content3Ref?.current) {
        experienceTL.from(content3Ref.current, {
          y: "3rem",
          delay: 0.25,
          clearProps: "all",
          scrollTrigger: {
            trigger: content3Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
      }

      if (content4Ref?.current) {
        experienceTL.from(content4Ref.current, {
          y: "3rem",
          delay: 0.25,
          clearProps: "all",
          scrollTrigger: {
            trigger: content4Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
      }
    },
    { scope: containerRef }
  );

  return (
    <>
      {!bitunix && (
        <>
          <section ref={containerRef} className="w-full relative">
            {/* huge logo background */}
            <Image
              src={hugeLogoBackgroundImg}
              alt="Logo"
              className="absolute top-0 left-1/2 transform -translate-x-1/2 z-[0] w-[95%] max-w-[1700px] huge-logo-bg"
            />
            {/*  */}
            <div className="container-lp mx-auto pt-[5rem] relative z-[1]">
              <div className="w-full flex flex-col items-center justify-center mx-auto mb-[4rem] lg:mb-0">
                <div
                  ref={content1Ref}
                  className="w-full md:w-4/5 lg:w-[75%] flex flex-col items-center justify-center mx-auto"
                >
                  {/* @ts-ignore */}
                  {data?.experienceTexts && (
                    <PortableText
                      // @ts-ignore
                      value={data?.experienceTexts ?? []}
                      components={customRichTextComponent({
                        block: {
                          h2: ({ children }) => (
                            <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.325rem] lg:tracking-[-0.485rem] leading-[110%] lg:leading-[8.8rem] text-center">
                              {children}
                            </h2>
                          ),
                          normal: ({ children }) => (
                            <p className="gradient-title text-[2.5rem] lg:text-[3.6rem] font-[600] leading-[4rem] lg:leading-[4rem] tracking-[-0.14rem] lg:tracking-[-0.2688rem] text-center mt-[2rem] mb-[3.2rem]">
                              {children}
                            </p>
                          ),
                        },
                        marks: {
                          orangeFlare: ({ children }) => (
                            <span
                              className={
                                bitunix
                                  ? "bitunix-shadow"
                                  : phemex
                                    ? "phemex-shadow"
                                    : "orange-shadow"
                              }
                            >
                              {children}
                            </span>
                          ),
                        },
                      })}
                    />
                  )}

                  {/* @ts-ignore */}
                  {data?.experienceCTA && (
                    // @ts-ignore
                    (<CTAButton data={data?.experienceCTA} />)
                  )}
                </div>
              </div>
            </div>
          </section>

          <div
            ref={hugeAssetImgRef}
            className="lg:mb-[40rem] lg:w-[95%] max-w-[1414px] mx-auto flex justify-center overflow-x-hidden relative z-[1]"
          >
            {/* @ts-ignore */}
            {data?.experienceImage && (
              <GroqImage
                // @ts-ignore
                src={data?.experienceImage}
                alt="System"
                className="lg:object-scale-down w-[150%] lg:w-full h-full max-w-max "
              />
            )}
          </div>
        </>
      )}
      <section
        ref={containerRef2}
        className="w-full relative bg-black mix-blend-exclusion overflow-x-clip"
      >
        <div
          className={`container-lp mx-auto ${
            bitunix ? "pt-[8rem] lg:pt-[14rem]" : "pt-[5rem] lg:py-[5rem]"
          }`}
        >
          <div
            ref={content2Ref}
            className="w-full md:w-4/5 lg:w-[65%] flex flex-col items-center justify-center mx-auto mb-[2.5rem] lg:mb-[3.5rem] relative"
          >
            {/* shadow/blur */}
            <Image
              src={blurImg}
              alt="blur background"
              className="w-[300%] max-w-max lg:w-full absolute left-[50%] top-0 lg:top-[-50%] translate-x-[-50%] translate-y-[-50%] z-[-1] pointer-events-none"
            />
            {/*  */}
            {data?.componentsTexts && (
              <PortableText
                value={data?.componentsTexts ?? []}
                components={customRichTextComponent({
                  block: {
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.325rem] lg:tracking-[-0.890rem] leading-[110%] lg:leading-[8.8rem] text-center mb-[2.4rem] w-[90%] md:w-[60%] lg:w-full">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-[2rem] font-[400] leading-[150%] text-white opacity-75 text-center mb-[2.4rem] w-full lg:w-[86%]">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    orangeFlare: ({ children }) => (
                      <span
                        className={
                          bitunix
                            ? "bitunix-shadow"
                            : phemex
                              ? "phemex-shadow"
                              : "orange-shadow"
                        }
                      >
                        {children}
                      </span>
                    ),
                  },
                })}
              />
            )}
          </div>

          <div
            ref={content3Ref}
            className="w-full flex flex-col lg:flex-row justify-center items-center gap-[2rem] lg:gap-[3rem] mb-[2rem] lg:mb-[3rem]"
          >
            <ExperienceCard
              cardNumber={1}
              cardTitle={data?.Components?.[0]?.title}
              cardImg={data?.Components?.[0]?.image}
              cardImgMobile={data?.Components?.[0]?.mobileImage}
            />
            <ExperienceCard
              cardNumber={2}
              cardTitle={data?.Components?.[1]?.title}
              cardImg={data?.Components?.[1]?.image}
              cardImgMobile={data?.Components?.[1]?.mobileImage}
            />
          </div>
          {/*  */}
          <div
            ref={content4Ref}
            className="w-full flex flex-col lg:flex-row justify-center items-center gap-[2rem] lg:gap-[3rem]"
          >
            <ExperienceCard
              cardNumber={3}
              cardTitle={data?.Components?.[2]?.title}
              cardImg={data?.Components?.[2]?.image}
              cardImgMobile={data?.Components?.[2]?.mobileImage}
            />
            <ExperienceCard
              cardNumber={4}
              cardTitle={data?.Components?.[3]?.title}
              cardImg={data?.Components?.[3]?.image}
              cardImgMobile={data?.Components?.[3]?.mobileImage}
            />
          </div>
        </div>
      </section>
    </>
  );
}
